import { useForm, SubmitHandler, SubmitErrorHandler } from 'react-hook-form';
import { useDispatch } from 'app/hooks';
import { Typography, Button, Input } from '@material-tailwind/react';
import { FormErrors } from 'common/components/FormError';
import { useTranslation } from 'react-i18next';
import { IUserRegisterRequest } from 'packages/shared/validate/types/user';
import { registerUserAsync } from 'features/users/slice';
import { useState } from 'react';
import { isValidateErrors } from 'packages/shared/validate/types/validate';

export const RegisterForm = () => {

    const { register, handleSubmit, reset, setError, formState: { errors }, setValue } = useForm<IUserRegisterRequest>({});

	const { t } = useTranslation();

    const dispatch = useDispatch();

	const [isSent, setIsSent] = useState(false);

	const onSubmit:SubmitHandler<IUserRegisterRequest> = (data, event) => {
		event?.preventDefault();

		const requestData:IUserRegisterRequest = {
			...data,
			...{
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
			}
		};

		dispatch(registerUserAsync(requestData))
			.unwrap()
			.then((_: any) => {
				reset();
				setIsSent(true);
			})
			.catch(error => {

				if (isValidateErrors(error)) {
					error.map(e => setError(e.name as keyof IUserRegisterRequest, {
						type: 'api',
						message: t('invalid', { ns: "form", name: t(e.name, { ns: "form" }) })
					}));
	
					return;
				}

				if (error?.hasOwnProperty("message") || error instanceof Error) {
					setError("root", {
						type: "api",
						message: t(error.message, { ns: "form" })
					});

					return;
				}

				setError("root", {
					type: "api",
					message: t("unknown error", { ns: "form" })
				});
			});
	};

	const onError:SubmitErrorHandler<IUserRegisterRequest> = (errors, event) => {
		Object.entries(errors).map(([name, error]) => {

			if (error.type === 'required') {

				setError(name as keyof IUserRegisterRequest, {
					type: 'required',
					message: t('required', { ns: 'form', name })
				});

				return;
			}
		});
	};

    return isSent 
		? (
			<div className="my-12 flex flex-col gap-4">
				<Typography variant="h6" color="blue-gray" className="-mb-3" placeholder={undefined} onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}}>
					{t("Form submitted")}
				</Typography>
				<Typography
					variant="small"
					color="blue-gray"
					className="font-normal opacity-70 truncate pb-2" 
					placeholder={undefined} 
					onPointerEnterCapture={undefined} 
					onPointerLeaveCapture={undefined}
				>
					{t("Email sent")}
				</Typography>
			</div>
		)
		: (
			<form autoComplete="off" className="m-auto mt-8 mb-2 w-80 max-w-screen-lg sm:w-96" onSubmit={handleSubmit(onSubmit, onError)}>
				<div className="mb-1 flex flex-col gap-4">
					<Typography variant="h6" color="blue-gray" className="-mb-3" placeholder={undefined} onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}}>
						{t("Organization")}
					</Typography>
					<div className="relative flex w-full max-w-[24rem]">
						<Input
							onPointerEnterCapture={()=>{}} 
							onPointerLeaveCapture={()=>{}} 
							id="team_name"
							error={!!errors.teamName}
							size="lg"
							className="focus:outline-none focus:ring-0"
							label={t("Organization")}
							crossOrigin={undefined}
							{...register("teamName")} 
						/>
					</div>
				</div>

				<div className="mb-1 flex flex-col gap-4">
					<Typography variant="h6" color="blue-gray" className="-mb-3" placeholder={undefined} onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}}>
						{t("Full name")}
					</Typography>
					<div className="relative flex w-full max-w-[24rem]">
						<Input
							onPointerEnterCapture={()=>{}} 
							onPointerLeaveCapture={()=>{}} 
							id="user_name"
							error={!!errors.userName}
							size="lg"
							className="focus:outline-none focus:ring-0"
							label={t("Full name")}
							crossOrigin={undefined}
							{...register("userName")} 
						/>
					</div>
				</div>

				<div className="mb-1 flex flex-col gap-4">
					<Typography variant="h6" color="blue-gray" className="-mb-3" placeholder={undefined} onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}}>
						{t("E-mail")}
					</Typography>
					<div className="relative flex w-full max-w-[24rem]">
						<Input
							onPointerEnterCapture={()=>{}} 
							onPointerLeaveCapture={()=>{}} 
							id="email"
							error={!!errors.email}
							size="lg"
							className="focus:outline-none focus:ring-0"
							label={t("E-mail")}
							crossOrigin={undefined}
							{...register("email")} 
						/>
					</div>
				</div>

				<div className="mb-1 flex flex-col gap-4">
					<Typography variant="h6" color="blue-gray" className="-mb-3" placeholder={undefined} onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}}>
						{t("Keystore ID")}
					</Typography>
					<div className="relative flex w-full max-w-[24rem]">
						<Input
							onPointerEnterCapture={()=>{}} 
							onPointerLeaveCapture={()=>{}} 
							id={t("Keystore ID")}
							error={!!errors.keystoreId}
							size="lg"
							className="focus:outline-none focus:ring-0"
							label={t("Keystore ID")}
							crossOrigin={undefined}
							{...register("keystoreId")}
						/>
					</div>
				</div>
				
				<FormErrors errors={errors} />

				<Button className="mt-6" 
					type="submit" 
					fullWidth 
					placeholder={undefined} 
					onPointerEnterCapture={()=>{}} 
					onPointerLeaveCapture={()=>{}}
				>
					{t("Register")}
				</Button>

			</form>
    	);
};
